import type { ReactNode } from 'react'
import { Box, Flex, Grid, Text } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import type { IconType } from 'react-icons'
import { LuInbox } from 'react-icons/lu'
import { fonts, mineral, shadows } from '../../theme'
import { reportAccent, TONES, type Tone } from './reportTheme'
import AnimatedNumber from '../brand/AnimatedNumber'

/**
 * KIT DE REPORTES DE ANALÍTICA — una sola forma para los siete reportes del menú.
 *
 *   <ReportPage>
 *     <ReportHeader icon title subtitle actions />   ← franja compacta, sin tarjeta
 *     <FilterBar> <FilterField label> … </FilterBar>  ← filtros en una fila blanca
 *     <VerdictBanner tone title … />                  ← el veredicto, tono suave
 *     <KpiGrid> <KpiTile …/> … </KpiGrid>              ← cifras clave
 *     <Panel title subtitle actions> gráfico/tabla </Panel>
 *   </ReportPage>
 *
 * Color: Analítica es AMATISTA (el acento del módulo). Los TONOS (ok / warn / bad /
 * info / neutral) son semánticos y salen de `TONES`: nunca un hex suelto en la página.
 * Gráficos: `chart` trae ejes, grilla, tooltip y paleta de minerales para recharts.
 */

export function ReportPage({ children }: { children: ReactNode }) {
  return <Flex direction="column" gap={5} pb={8}>{children}</Flex>
}

export function ReportHeader({ icon: Icon, title, subtitle, actions }: {
  icon: IconType; title: string; subtitle?: ReactNode; actions?: ReactNode
}) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <Flex align={{ base: 'flex-start', lg: 'center' }} direction={{ base: 'column', lg: 'row' }} gap={4}>
        <Flex align="center" gap={3} minW={0} flex={1}>
          <Flex w="38px" h="38px" borderRadius="11px" align="center" justify="center" bg={reportAccent.color} color="#fff" flexShrink={0}>
            <Icon size={19} />
          </Flex>
          <Box minW={0}>
            <Text fontFamily={fonts.display} fontWeight={800} fontSize={{ base: '22px', md: '25px' }} letterSpacing="-0.03em" color={mineral.ink} lineHeight="1.1">
              {title}
            </Text>
            {subtitle && <Text fontSize="13px" color={mineral.ink3} mt={0.5} lineClamp={2}>{subtitle}</Text>}
          </Box>
        </Flex>
        {actions && <Flex gap={2} wrap="wrap" flexShrink={0}>{actions}</Flex>}
      </Flex>
    </motion.div>
  )
}

/** Botón de acción del reporte. `primary` = terracota plano (la acción principal, una sola). */
export function ActionButton({ icon: Icon, label, onClick, variant = 'secondary', loading, disabled, title }: {
  icon?: IconType; label: string; onClick?: () => void; variant?: 'primary' | 'secondary' | 'ghost'
  loading?: boolean; disabled?: boolean; title?: string
}) {
  const off = disabled || loading
  const styles = variant === 'primary'
    ? { bg: mineral.ocreDeep, color: '#fff', border: '1px solid transparent', hover: { bg: mineral.ocreDark } }
    : variant === 'ghost'
      ? { bg: 'transparent', color: mineral.ink2, border: '1px solid transparent', hover: { bg: mineral.sunk, color: mineral.ink } }
      : { bg: '#fff', color: mineral.ink2, border: `1px solid ${mineral.line}`, hover: { borderColor: reportAccent.color, color: reportAccent.deep } }
  return (
    <Flex as="button" {...{ type: 'button', disabled: off, title }} align="center" gap={2} h="38px" px={3.5} borderRadius="10px"
      fontSize="13px" fontWeight={600} whiteSpace="nowrap" bg={styles.bg} color={styles.color} border={styles.border}
      cursor={off ? 'not-allowed' : 'pointer'} opacity={disabled && !loading ? 0.5 : 1}
      _hover={off ? {} : styles.hover} transition="all .15s" onClick={off ? undefined : onClick}>
      {Icon && (
        <motion.span animate={loading ? { rotate: 360 } : { rotate: 0 }}
          transition={loading ? { duration: 0.9, repeat: Infinity, ease: 'linear' } : { duration: 0.2 }} style={{ display: 'flex' }}>
          <Icon size={15} />
        </motion.span>
      )}
      {label}
    </Flex>
  )
}

export function FilterBar({ children, note }: { children: ReactNode; note?: ReactNode }) {
  return (
    <Box bg={mineral.surface} border={`1px solid ${mineral.line}`} borderRadius="16px" px={5} py={4} boxShadow={shadows.sm}>
      <Flex gap={4} wrap="wrap" align="flex-end">{children}</Flex>
      {note && <Text fontSize="12px" color={mineral.ink3} mt={3}>{note}</Text>}
    </Box>
  )
}

export function FilterField({ label, children, width }: { label: string; children: ReactNode; width?: string | object }) {
  return (
    <Box w={width ?? '180px'} minW={0}>
      <Text fontSize="11px" fontWeight={600} color={mineral.ink3} mb={1.5} textTransform="uppercase" letterSpacing="0.06em">{label}</Text>
      {children}
    </Box>
  )
}

/** El veredicto del reporte: franja suave con el tono, no un bloque saturado. */
export function VerdictBanner({ tone, title, subtitle, meta }: { tone: Tone; title: string; subtitle?: ReactNode; meta?: ReactNode }) {
  const t = TONES[tone]
  const Icon = t.icon
  return (
    <motion.div initial={{ opacity: 0, scale: 0.99 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25 }}>
      <Flex align="center" gap={4} px={5} py={4} borderRadius="16px" bg={t.soft} border={`1px solid ${t.color}40`} position="relative" overflow="hidden">
        <Box position="absolute" left={0} top={0} bottom={0} w="4px" bg={t.color} />
        <Flex w="40px" h="40px" borderRadius="12px" align="center" justify="center" bg={t.color} color="#fff" flexShrink={0}>
          <Icon size={20} />
        </Flex>
        <Box flex={1} minW={0}>
          <Text fontFamily={fonts.display} fontWeight={700} fontSize="17px" color={t.deep} lineHeight="1.2">{title}</Text>
          {subtitle && <Text fontSize="13px" color={mineral.ink2} mt={0.5}>{subtitle}</Text>}
        </Box>
        {meta && <Box flexShrink={0} textAlign="right" fontSize="13px" color={mineral.ink2}>{meta}</Box>}
      </Flex>
    </motion.div>
  )
}

export function KpiGrid({ children, cols = 4 }: { children: ReactNode; cols?: number }) {
  return (
    <Grid templateColumns={{ base: 'repeat(2, minmax(0, 1fr))', lg: `repeat(${cols}, minmax(0, 1fr))` }} gap={3}>
      {children}
    </Grid>
  )
}

/**
 * Cifra clave. `value` numérico anima (cuenta hasta el valor); `format` decide cómo se
 * imprime (moneda, %, unidades). Si el dato no existe, pasar `null`: se muestra "—",
 * nunca un 0 inventado.
 */
export function KpiTile({ label, value, format, unit, hint, tone, icon: Icon, index = 0 }: {
  label: string; value: number | string | null; format?: (n: number) => string; unit?: string
  hint?: ReactNode; tone?: Tone; icon?: IconType; index?: number
}) {
  const t = tone ? TONES[tone] : null
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 * index, duration: 0.25 }} style={{ minWidth: 0 }}>
      <Box h="100%" bg={mineral.surface} border={`1px solid ${mineral.line}`} borderRadius="16px" px={4} py={3.5} boxShadow={shadows.sm}
        position="relative" overflow="hidden">
        {t && <Box position="absolute" left={0} top={0} right={0} h="3px" bg={t.color} />}
        <Flex align="center" gap={2} color={mineral.ink3}>
          {Icon && <Box color={t?.color ?? reportAccent.color} display="flex"><Icon size={14} /></Box>}
          <Text fontSize="12px" fontWeight={500} lineClamp={1}>{label}</Text>
        </Flex>
        <Text fontFamily={fonts.display} fontWeight={800} fontSize="26px" letterSpacing="-0.03em" lineHeight="1.15" mt={1.5}
          color={t?.deep ?? mineral.ink} lineClamp={1} title={typeof value === 'number' && format ? format(value) : undefined}>
          {value === null || value === undefined
            ? <Text as="span" color={mineral.ink4}>—</Text>
            : typeof value === 'number'
              ? <AnimatedNumber value={value} format={format} />
              : value}
          {unit && value !== null && <Text as="span" fontSize="14px" fontWeight={600} color={mineral.ink3} ml={1}>{unit}</Text>}
        </Text>
        {hint && <Text fontSize="11.5px" color={mineral.ink3} mt={1} lineClamp={2}>{hint}</Text>}
      </Box>
    </motion.div>
  )
}

export function Panel({ title, subtitle, actions, children, padded = true, index = 0 }: {
  title?: ReactNode; subtitle?: ReactNode; actions?: ReactNode; children: ReactNode; padded?: boolean; index?: number
}) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * index, duration: 0.3 }} style={{ minWidth: 0 }}>
      <Box bg={mineral.surface} border={`1px solid ${mineral.line}`} borderRadius="18px" boxShadow={shadows.sm} overflow="hidden" h="100%">
        {(title || actions) && (
          <Flex align="flex-start" justify="space-between" gap={3} px={5} pt={4} pb={padded ? 0 : 4}
            borderBottom={padded ? 'none' : `1px solid ${mineral.line}`}>
            <Box minW={0}>
              {title && <Text fontFamily={fonts.display} fontWeight={700} fontSize="16px" color={mineral.ink} letterSpacing="-0.01em">{title}</Text>}
              {subtitle && <Text fontSize="12.5px" color={mineral.ink3} mt={0.5}>{subtitle}</Text>}
            </Box>
            {actions && <Flex gap={2} flexShrink={0}>{actions}</Flex>}
          </Flex>
        )}
        <Box px={padded ? 5 : 0} py={padded ? 4 : 0}>{children}</Box>
      </Box>
    </motion.div>
  )
}

/** Etiqueta de estado (tablas, leyendas). */
export function ToneBadge({ tone, children }: { tone: Tone; children: ReactNode }) {
  const t = TONES[tone]
  return (
    <Box as="span" display="inline-flex" alignItems="center" gap={1.5} px={2} py="2px" borderRadius="7px" bg={t.soft} color={t.deep}
      fontSize="10.5px" fontWeight={700} textTransform="uppercase" letterSpacing="0.04em" whiteSpace="nowrap">
      <Box w="6px" h="6px" borderRadius="full" bg={t.color} />
      {children}
    </Box>
  )
}

export function Notice({ tone = 'info', children }: { tone?: Tone; children: ReactNode }) {
  const t = TONES[tone]
  const Icon = t.icon
  return (
    <Flex align="flex-start" gap={2.5} px={4} py={3} borderRadius="12px" bg={t.soft} color={mineral.ink2} fontSize="13px">
      <Box color={t.color} display="flex" mt="2px" flexShrink={0}><Icon size={15} /></Box>
      <Box>{children}</Box>
    </Flex>
  )
}

export function EmptyState({ title, hint, icon: Icon = LuInbox }: { title: string; hint?: ReactNode; icon?: IconType }) {
  return (
    <Flex direction="column" align="center" justify="center" textAlign="center" py={12} px={6} gap={2}>
      <Flex w="52px" h="52px" borderRadius="16px" align="center" justify="center" bg={reportAccent.soft} color={reportAccent.color}>
        <Icon size={24} />
      </Flex>
      <Text fontFamily={fonts.display} fontWeight={700} fontSize="15px" color={mineral.ink}>{title}</Text>
      {hint && <Text fontSize="13px" color={mineral.ink3} maxW="420px">{hint}</Text>}
    </Flex>
  )
}

/** Esqueleto de carga para la primera carga del reporte. */
export function ReportSkeleton() {
  return (
    <Flex direction="column" gap={4}>
      <Grid templateColumns={{ base: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={3}>
        {Array.from({ length: 4 }).map((_, i) => <Box key={i} h="96px" borderRadius="16px" bg={mineral.sunk} opacity={1 - i * 0.12} />)}
      </Grid>
      <Box h="320px" borderRadius="18px" bg={mineral.sunk} opacity={0.7} />
    </Flex>
  )
}

